import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/**
 * AD-220/221: "Let Us Design It" — the customer's briefing (studio.v1.api.design_requests.*).
 */
const DESIGN_REQUEST_KEY = 'api-v1-studio-design-request';

const queryDesignRequest = (id, options = {}) => {

    return useQuery({
        queryKey: [DESIGN_REQUEST_KEY, id],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.design_requests.show', id));
            return data?.data;
        },
        enabled: Boolean(id),
        ...options,
    });
}

const mutateDesignRequestCreate = () => {

    return useMutation({
        mutationKey: ['design-request-create'],
        mutationFn: async (payload) => axios.post(route('studio.v1.api.design_requests.store'), payload),
    });
}

/**
 * Saving and submitting answer with the whole briefing, which is seeded into the query.
 */
const useBriefingWrite = (id, routeName, method) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [routeName, id],
        mutationFn: async (payload) => axios[method](route(routeName, id), payload),
        onSuccess: (res) => {
            if (res?.data?.data) {
                // The file list is the upload/remove mutations' to keep: a save
                // answered after an upload must not drop the new file again.
                queryClient.setQueryData([DESIGN_REQUEST_KEY, id], (cached) => ({
                    ...res.data.data,
                    files: cached?.files ?? res.data.data.files,
                }));
            }
        },
    });
}

/**
 * "Request Ad Design": the ad becomes the briefing (AD-225, answer 2).
 */
const mutateDesignRequestFromAd = (adId) => {

    return useMutation({
        // AD-259: a new ad has its id only once the editor has saved it, so the id is the
        // call's, not the key's: a changed key resets the mutation and drops mutate()'s
        // onSuccess (the briefing redirect).
        mutationKey: ['design-request-from-ad'],
        mutationFn: async (id = adId) => axios.post(route('studio.v1.api.design_requests.from-ad', { ad: id })),
    });
}

const mutateDesignRequestUpdate = (id) => useBriefingWrite(id, 'studio.v1.api.design_requests.update', 'put');

const mutateDesignRequestSubmit = (id) => useBriefingWrite(id, 'studio.v1.api.design_requests.submit', 'put');

const mutateDesignRequestFileUpload = (id) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['design-request-file-upload', id],
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append('file', file);

            return axios.post(route('studio.v1.api.design_requests.files.store', id), formData);
        },
        onSuccess: (res) => {
            queryClient.setQueryData([DESIGN_REQUEST_KEY, id], (designRequest) => designRequest && ({
                ...designRequest,
                files: [...(designRequest.files || []), res.data.data],
            }));
        },
    });
}

const mutateDesignRequestFileDelete = (id) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['design-request-file-delete', id],
        mutationFn: async (fileId) => axios.delete(route('studio.v1.api.design_requests.files.destroy', { designRequest: id, file: fileId })),
        onSuccess: (_res, fileId) => {
            queryClient.setQueryData([DESIGN_REQUEST_KEY, id], (designRequest) => designRequest && ({
                ...designRequest,
                files: (designRequest.files || []).filter((file) => file.id !== fileId),
            }));
        },
    });
}

/**
 * AD-224: "Request Changes" from the Design Feedback page — the feedback with its
 * files and link, for the sending the email link belongs to.
 */
const mutateDesignRequestChanges = (id) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['design-request-changes', id],
        mutationFn: async ({ round, sent, feedback_text, feedback_link, files = [] }) => {
            const formData = new FormData();
            formData.append('round', round);
            formData.append('sent', sent);
            formData.append('feedback_text', feedback_text);
            if (feedback_link?.trim()) {
                formData.append('feedback_link', feedback_link.trim());
            }
            files.forEach((file) => formData.append('files[]', file));

            return axios.post(route('studio.v1.api.design_requests.request-changes', id), formData);
        },
        onSuccess: (res) => {
            if (res?.data?.data) {
                queryClient.setQueryData([DESIGN_REQUEST_KEY, id], res.data.data);
            }
        },
    });
}

/**
 * AD-224: "Approve Design" on the Design Feedback page; answers with where to go
 * next (Delivery).
 */
const mutateDesignRequestApprove = (id) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['design-request-approve', id],
        mutationFn: async ({ round, sent }) => axios.post(route('studio.v1.api.design_requests.approve', id), { round, sent }),
        onSuccess: (res) => {
            if (res?.data?.data) {
                queryClient.setQueryData([DESIGN_REQUEST_KEY, id], res.data.data);
            }
        },
    });
}

/**
 * AD-222: the designers' side — one brief (Design Brief / Feedback Brief modals),
 * Send Approval and Publish. Both actions refresh the Design Request list.
 */
const ADMIN_BRIEF_KEY = 'api-v1-studio-admin-design-brief';
const ADMIN_TABLE_KEY = ['ssr-datatable-admin-design-requests'];

const queryAdminDesignBrief = (id, options = {}) => {

    return useQuery({
        queryKey: [ADMIN_BRIEF_KEY, id],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.admin.design_requests.show', id));
            return data?.data;
        },
        enabled: Boolean(id),
        ...options,
    });
}

const useAdminDesignAction = (routeName) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [routeName],
        mutationFn: async (id) => axios.put(route(routeName, id)),
        onSuccess: (res, id) => {
            if (res?.data?.data) {
                queryClient.setQueryData([ADMIN_BRIEF_KEY, id], res.data.data);
            }
            queryClient.invalidateQueries({ queryKey: ADMIN_TABLE_KEY });
        },
    });
}

const mutateAdminSendApproval = () => useAdminDesignAction('studio.v1.api.admin.design_requests.send-approval');

const mutateAdminPublish = () => useAdminDesignAction('studio.v1.api.admin.design_requests.publish');

export {
    queryDesignRequest,
    mutateDesignRequestCreate,
    mutateDesignRequestFromAd,
    mutateDesignRequestUpdate,
    mutateDesignRequestSubmit,
    mutateDesignRequestFileUpload,
    mutateDesignRequestFileDelete,
    mutateDesignRequestChanges,
    mutateDesignRequestApprove,
    queryAdminDesignBrief,
    mutateAdminSendApproval,
    mutateAdminPublish,
}
